import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import AccountClosureModel from '../../../../../../services/accountClosure.model';
import { AccountClosureService } from 'src/app/services/accountClosure.service';
import BusinessEntity from '../../../../../../services/businessEntity.model';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import * as moment from 'moment';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { ToastrService } from 'ngx-toastr';
import { NotificationUpdateService } from 'src/app/services/notificationsUpdate.service';

declare var $: any;


/**
 * @component AccountClosureComponent
 * @selector app-account-closure
 *
 * @description
 * The `AccountClosureComponent` manages the lifecycle status of a business account within the application.
 * It provides an interface for administrators or authorized users to view the current status of a business account,
 * see historical request details (who requested closure/inactivation and when), and perform actions to change the account status.
 *
 * ## Key Responsibilities:
 * 1. **Status Management**: Allows users to Inactivate, Close, or Re-activate (Cancel closure/inactivation) a business account.
 * 2. **Data Visualization**: Displays key business details such as the business owner, operating duration, and details of the last status change request.
 * 3. **Logic Handling**: Determines which actions are available based on the current `accountStatus` of the business entity.
 * 4. **Audit Trail**: Fetches and displays information about who initiated the last account status change.
 * 5. **Confirmation Workflow**: Implements a confirmation modal flow before executing critical status changes.
 *
 * ## User Roles & Permissions:
 * - **Business Admin / Farmer**: Typically the users who own the account or have high-level privileges to manage account status.
 * - **Admin**: System administrators who might be viewing or managing these accounts.
 *
 * ## LocalStorage Dependencies:
 * - `LoginUserName`: Used to set the requested person name when viewing closed/inactivated accounts in specific contexts.
 * - `personID`: Used to track who is performing the update in the `stateTrack` object.
 * - `businessEntityById`: Updated upon successful status change to reflect the new state immediately in the session.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` subscribes to route parameters to get the Business ID.
 * 2. **Loading**: `onLoad` fetches the `BusinessEntity` and `AccountClosure` history. It calculates operating days and determines the "Requested By" and "Business Owner" names.
 * 3. **Action Selection**: User selects an action (Inactivate, Close, Cancel). `confirmationAlert` sets up the modal.
 * 4. **Submission**: `onSubmittingTheRequest` constructs the payload with the new status and audit trail (`stateTrack`), sends it to the API, and updates the local session.
 *
 * @service BusinessEntityService - For fetching business details.
 * @service AccountClosureService - For fetching account history and updating status.
 * @service CommonMethodsService - For error handling.
 * @service NotificationUpdateService - For notifying other components of changes.
 */
@Component({
  selector: 'app-account-closure',
  templateUrl: './account-closure.component.html',
  styleUrls: ['./account-closure.component.scss']
})
export class AccountClosureComponent {

  /** @type {BusinessEntity} The business entity object being managed. */
  public businessEntity: BusinessEntity;

  /** @type {string} The ID of the business retrieved from route parameters. */
  public paramsBusinessId: string;

  /** @type {string} Formatted string showing how long the business has been operating. */
  public operatingSinceDays: string;

  /** @type {AccountClosureModel} Model used for constructing the update payload. */
  public accoutClosureModel: AccountClosureModel;

  /** @type {boolean} Flag for page loading state. */
  public pageLoader: boolean = false;

  /** @type {Object} Object to hold account details (unused in current logic). */
  public aquCloudAccount: Object;

  /** @type {string} Name of the person who requested the last status change. */
  public requestedPersonName: string = '';

  /** @type {string} Name of the business owner (Business Admin or Farmer). */
  public businessOwnerName: string = '';

  /** @type {any} Date and time of the last status change request. */
  public requestedDateTime: any = undefined;

  /** @type {string} Main message for the confirmation modal. */
  public message: string;

  /** @type {string} Sub-message/instruction for the confirmation modal. */
  public subMessage: string;

  /** @type {string} Success message displayed after an update. */
  public sucessMessage: string;

  /** @type {string} Sub-message for success popup (unused). */
  public sucessSubMessage: string;

  /** @type {string} Label for the submit button in the confirmation modal. */
  public submitButtonName: string;

  /** @type {string} Label for the cancel button in the confirmation modal. */
  public cancelButtonName: string;

  /** @type {any} List of business persons (unused). */
  public businessPersons: any;

  /** @type {boolean} Flag to disable the request button during processing. */
  public requestBtnEnableDisable: boolean = false;

  /** @type {boolean} Flag to disable the cancel button during processing. */
  public cancelBtnEnableDisable: boolean = false;

  /** @type {string} Stores the previous business ID to prevent redundant reloads. */
  public previousBusinessId: string;

  /** @type {string} The action selected by the user ('cancel', 'inactivate', 'close'). */
  public selectedOption: string;

  /** @type {boolean} Flag indicating if the 'Activate' (Cancel request) option should be available. */
  public activateAccount: boolean;

  /** @type {boolean} Flag indicating if the 'Inactivate' option should be available. */
  public inactivateAccount: boolean;

  /** @type {boolean} Flag indicating if the 'Close' option should be available. */
  public closeAccount: boolean;

  /**
   * Constructor for AccountClosureComponent.
   * Initializes services and models.
   *
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {ActivatedRoute} route - Service for retrieving route parameters.
   * @param {AccountClosureService} accountClosureService - Service for account closure operations.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {ToastrService} toastr - Service for toast notifications.
   * @param {Router} router - Angular Router.
   * @param {NotificationUpdateService} notificationsUpdateService - Service to trigger UI updates.
   */
  constructor(public businessEntityService: BusinessEntityService, public route: ActivatedRoute, public accountClosureService: AccountClosureService,
    public commonMethodsService: CommonMethodsService, public toastr: ToastrService, public router: Router, private notificationsUpdateService: NotificationUpdateService) {
    this.accoutClosureModel = new AccountClosureModel();
    this.businessEntity = new BusinessEntity();
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Subscribes to route parameters to detect changes in the Business ID.
   * Triggers `onLoad` if the ID changes.
   */
  public async ngOnInit(): Promise<void> {
    this.route.paramMap.subscribe(params => {
      // React to changes in the route parameters
      this.paramsBusinessId = params.get('id');
      // Perform any additional logic or data fetching based on the new parameter values
      if (this.previousBusinessId != this.paramsBusinessId) {
        this.onLoad();
      }
    });
  }

  /**
   * @method onLoad
   * @async
   * @description
   * Loads the business entity details and account closure history.
   * - Fetches business entity by ID.
   * - Fetches account closure details to determine who requested the last action and when.
   * - Calculates the operating duration of the business.
   * - Sets the visibility flags (`activateAccount`, `inactivateAccount`, `closeAccount`) based on the current `accountStatus`.
   */
  public async onLoad() {
    try {
      this.pageLoader = false;
      this.businessEntity = await this.businessEntityService.getOneByID(this.paramsBusinessId);
      this.previousBusinessId = this.paramsBusinessId;
      if (this.businessEntity) {
        const aquCloudAccounts = await this.accountClosureService.getBusinessPersonsByAccountId(this.businessEntity.accountID);
        if (aquCloudAccounts?.length > 0) {
          if (['inactivated', 'closed'].includes(this.businessEntity?.accountStatus)) {
            const stateTrackObject = aquCloudAccounts?.[0]?.stateTrack[aquCloudAccounts?.[0]?.stateTrack.length - 1];
            const clientPersons = aquCloudAccounts?.[0]?.business?.persons?.filter((personObj: any) =>  ['Business Admin', 'Farmer'].includes(personObj?.personID?.personType?.[0]?.name));
            this.requestedDateTime = stateTrackObject[Object.keys(stateTrackObject).find(k => k.endsWith("On"))];
            this.requestedPersonName = localStorage.getItem("LoginUserName");
            if (clientPersons?.length > 0) {
              const baPerson = clientPersons.find((baPersonObj: any) => baPersonObj?.personID?.personType?.[0]?.name == 'Business Admin');
              if (baPerson) {
                this.businessOwnerName = baPerson?.personID?.firstName + ' ' + baPerson?.personID?.lastName;
              } else {
                const ownerPerson = clientPersons?.filter((baPersonObj: any) => baPersonObj?.personID?.personType?.[0]?.name == 'Farmer');
                if (ownerPerson?.length > 0) {
                  this.businessOwnerName = ownerPerson?.[0]?.personID?.firstName + ' ' + ownerPerson?.[0]?.personID?.lastName;
                }
              }
            }
          } else if (this.businessEntity?.accountStatus == 'markedForInactivation') {
            const stateTrackObject = aquCloudAccounts?.[0]?.stateTrack[aquCloudAccounts?.[0]?.stateTrack.length - 1];
            const personObject = aquCloudAccounts?.[0]?.business?.persons?.find((personObj: any) => personObj?.personID?.id == stateTrackObject[Object.keys(stateTrackObject).find(k => k.endsWith("By"))]);
            const baPersonObject = aquCloudAccounts?.[0]?.business?.persons?.find((personObj: any) => personObj?.personID?.personType?.[0]?.name == 'Business Admin');
            if (personObject || stateTrackObject) {
              this.requestedPersonName = personObject?.personID?.firstName + ' ' + personObject?.personID?.lastName;
              this.requestedDateTime = stateTrackObject[Object.keys(stateTrackObject).find(k => k.endsWith("On"))];
            }
            if (baPersonObject) {
              this.businessOwnerName = baPersonObject?.personID?.firstName + ' ' + baPersonObject?.personID?.lastName;
            } else {
              this.businessOwnerName = personObject?.personID?.firstName + ' ' + personObject?.personID?.lastName;
            }
          } else {
            const clientPersons = aquCloudAccounts?.[0]?.business?.persons?.filter((personObj: any) => ['Business Admin', 'Farmer'].includes(personObj?.personID?.personType?.[0]?.name));
            if (clientPersons?.length > 0) {
              const baPerson = clientPersons?.find((obj: any) => obj?.personID?.personType?.[0]?.name == 'Business Admin');
              const FarmerPerson = clientPersons?.filter((obj: any) => obj?.personID?.personType?.[0]?.name == 'Farmer');
              if (baPerson) {
                this.businessOwnerName = baPerson?.personID?.firstName + ' ' + baPerson?.personID?.lastName;
              } else {
                this.businessOwnerName = FarmerPerson?.[0]?.personID?.firstName + ' ' + FarmerPerson?.[0]?.personID?.lastName;
              }
            }
          }
        }
        // Converting onboarded date to date object
        const onBoardedDateTime = new Date(this.businessEntity.createdAt);
        // getting current date
        const currentDate = new Date();
        // calculate the time difference in milliseconds
        const timeDifference = currentDate.getTime() - onBoardedDateTime.getTime();
        // calculate years, months, and days
        const years = Math.floor(timeDifference / (365.25 * 24 * 60 * 60 * 1000));
        const months = Math.floor((timeDifference % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
        const days = Math.floor((timeDifference % (30.44 * 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000));
        this.operatingSinceDays = years + ' Years ' + months + ' months ' + days + ' days ';
      }
      if (this.businessEntity.accountStatus == 'active') {
        this.activateAccount = false;
        this.inactivateAccount = true;
        this.closeAccount = true;
      } else if (this.businessEntity.accountStatus == 'inactivated') {
        this.activateAccount = true;
        this.inactivateAccount = false;
        this.closeAccount = true;
      } else if (this.businessEntity.accountStatus == 'closed') {
        this.activateAccount = true;
        this.inactivateAccount = true;
        this.closeAccount = false;
      } else {
        this.activateAccount = true;
        this.inactivateAccount = true;
        this.closeAccount = true;
      }
      this.pageLoader = true;
      this.selectedOption = undefined;
    } catch (error) {
      this.pageLoader = false;
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onSubmittingTheRequest
   * @async
   * @description
   * Handles the submission of the account status change request.
   * - Constructs the payload based on `selectedOption` ('cancel', 'inactivate', 'close').
   * - Updates the `stateTrack` with the current user and timestamp.
   * - Calls the API to update the account status.
   * - Updates `localStorage` and shows a success modal upon completion.
   */
  public async onSubmittingTheRequest() {
    try {
      this.requestBtnEnableDisable = true;
      this.cancelBtnEnableDisable = true;
      const currentDate = new Date();
      const fromYear = currentDate.getFullYear() > 0 ? currentDate.getFullYear() : currentDate.getFullYear() * -1;
      const formattedDate = `${fromYear}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
      const formatedTime = moment(currentDate, ['h:mm A']).format('HH:mm');
      const statusMap = {
        cancel: { status: 1, key: 'activated', message: 'Re-activated'},
        inactivate: { status: 3, key: 'inactivated', message: 'De-activated'},
        close: { status: 4, key: 'closed', message: 'closed'},
      };

      const { status, key, message } = statusMap[this.selectedOption];
      const stateTrackKey = `${key}On`;
      const stateTrackBy = `${key}By`;

      this.accoutClosureModel = {
        businessID: this.businessEntity.id,
        status,
        updatedBy: localStorage.getItem('personID'),
        stateTrack: {
          [stateTrackKey]: formattedDate + ' ' + formatedTime,
          [stateTrackBy]: localStorage.getItem('personID'),
        },
        appName: 'blubay'
      };

      delete this.accoutClosureModel.updatedBy;
      delete this.accoutClosureModel.stateTrack.activatedBy;
      delete this.accoutClosureModel.stateTrack.inactivatedBy;
      delete this.accoutClosureModel.stateTrack.closedBy;

      const businessAccountStatusUpdated = await this.accountClosureService.updateOneByID(
        this.businessEntity.accountID,
        this.accoutClosureModel
      );

      if (businessAccountStatusUpdated && businessAccountStatusUpdated.updated) {
        const updatedBusiness = JSON.parse(localStorage.getItem('businessEntityById'));
        updatedBusiness.accountStatus = status === 1 ? 'active' : (status === 3 ? 'inactivated' : 'closed');
        localStorage.setItem('businessEntityById', JSON.stringify(updatedBusiness));
        $('#notification').modal('hide');
        this.sucessMessage = `Account ${message} successfully`;
        $("#sucessNotification").modal({
          backdrop: 'static',
          keyboard: false,
          show: true,
        });
      }
    } catch (error) {
      this.requestBtnEnableDisable = true;
      this.cancelBtnEnableDisable = true;
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
   }

  /**
   * @method confirmationAlert
   * @description
   * Configures and opens the confirmation modal based on the `selectedOption`.
   */
  public confirmationAlert() {
    switch (this.selectedOption) {
      case 'cancel':
        this.message = "Your are cancelling the closure request or Re-activating the account";
        this.subMessage = "Click 'Yes' to continue and 'No' to cancel";
        this.submitButtonName = 'No';
        this.cancelButtonName = 'Yes';
        break;

      case 'inactivate':
        this.message = "You are De-activating the account";
        this.subMessage = "Click 'De activate' to proceed and 'Cancel' to no.";
        this.submitButtonName = 'De activate';
        this.cancelButtonName = 'Cancel';
        break;

      default:
        this.message = "You are closing the account";
        this.subMessage = "Click 'Close' to proceed and 'Cancel' to No";
        this.submitButtonName = "Close";
        this.cancelButtonName = 'Cancel';
        break;
    }

    $("#notification").modal({
      backdrop: 'static',
      keyboard: false,
      show: true
    });
  }

  /**
   * @method onCancelingTheRequest
   * @description
   * Closes the confirmation modal and resets the selected option.
   */
  public onCancelingTheRequest() {
    $('#notification').modal('hide');
    this.selectedOption = undefined;
  }

  /**
   * @method onCloseSucessfullPopup
   * @description
   * Closes the success modal, triggers a notification update, and navigates back to the business list.
   */
  public onCloseSucessfullPopup() {
    $('#sucessNotification').modal('hide');
    this.notificationsUpdateService.notifyUpdate();
    this.router.navigate(['/home/Business-Entity/Business']);
  }

  /**
   * @method convertTo24Hrs
   * @description
   * Converts a timestamp string to a 12-hour format with AM/PM.
   * @param {any} timeStamp - The timestamp string.
   * @returns {string} Formatted time string.
   */
  public convertTo24Hrs(timeStamp: any) {
    const time = timeStamp.split('T')[1];
    const _12hrs = moment(time, ['HH:mm']).format('h:mm A');
    return _12hrs;
  }
}
